import { HttpClientOptions } from "./interfaces";
import { logger } from "./logger";
export { logger };
export const API_V2_URL = process.env.NEXT_PUBLIC_API_V2_URL;

// ponytail: heuristic stack scrape, not a true request-scoped route id — swap for
// AsyncLocalStorage-based route context if this misses frames under minified prod bundles.
function findCallerRoute(stack?: string): string | undefined {
    if (!stack) return undefined;
    for (const line of stack.split("\n").slice(1)) {
        if (line.includes("/utilities/index.ts") || line.includes("/utilities/apis.ts") || line.includes("node_modules")) continue;
        const match = line.match(/(app[\\/][^\s):]+)/);
        if (match) return match[1];
    }
    return undefined;
}

async function _fetch(baseEnvKey: string, url: string, options: HttpClientOptions = {}) {
    const { method = "GET" } = options;
    const isGet = method.toUpperCase() === "GET";
    try {
        const base = (baseEnvKey || "").replace(/\/$/, "");
        if (!base) {
            return { status: "error", message: "API base URL not configured" };
        }
        const normalizedPath = url.startsWith("/") ? url : `/${url}`;
        const fullUrl = `${base}${normalizedPath}`;
        const res = await fetch(fullUrl, {
            method: options.method || "GET",
            headers: {
                "Content-Type": "application/json",
                "x-api-key": process.env.NEXT_PUBLIC_API_KEY || "carjunction",
                ...options.headers,
            },
            body: options.body ? JSON.stringify(options.body) : undefined,
            ...(isGet
            ? process.env.NODE_ENV === "development"
                ? { cache: "no-store" as const }
                : { next: { revalidate: 300 } }
            : { cache: "no-store" as const }),
        });
        if (!res.ok) {
            if (res.status === 404) {
                return { status: "error", message: `Not found: ${url}` };
            }
            const err = new Error(`Failed to fetch data from ${url}. Status: ${res.status}`) as Error & { statusCode?: number };
            err.statusCode = res.status;
            throw err;
        }
        return res.json();
    } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        const digest = (e as { digest?: string })?.digest;
        if (digest === 'DYNAMIC_SERVER_USAGE' || msg.includes('Dynamic server usage')) throw e;
        const statusCode = (e as { statusCode?: number })?.statusCode;
        logger.error(`API Error: ${url}`, e, {
            url,
            options,
            statusCode,
            errorClass: statusCode ? (statusCode >= 500 ? "5xx" : statusCode >= 400 ? "4xx" : undefined) : "network",
            route: findCallerRoute((e as Error)?.stack),
        });
        return { status: "error", message: msg, statusCode };
    }
}

export async function httpClientV2(url: string, options: HttpClientOptions = {}) {
    const res = await _fetch(process.env.NEXT_PUBLIC_API_V2_URL || "", url, options);
    // v2 wraps all success responses with { success: true, ... } instead of { status: 'success', ... }.
    // Add status field so existing consumer code (status === 'success') keeps working.
    if (res && typeof res === "object" && "success" in res && !("status" in res)) {
        return { status: res.success ? "success" : "error", ...res };
    }
    return res;
}

// Updated utilities
export function processCarSpecs(modelData: any): any[] {
    const out: any[] = [];

    // Unwrap common wrappers
    const src = modelData?.modelData ?? modelData?.model ?? modelData;

    // If caller passed a flat array of specs (legacy shape), normalize it
    if (Array.isArray(src)) {
        for (const spec of src) {
            const label = spec.label || spec.name || spec.key || spec.title;
            if (!label) continue;

            let value: any = null;
            if (spec.value !== undefined && spec.value !== null && String(spec.value).trim() !== "") {
                value = spec.value;
            } else if (spec.valueNormalized !== undefined && spec.valueNormalized !== null) {
                value = spec.unit ? `${spec.valueNormalized} ${spec.unit}` : String(spec.valueNormalized);
            } else {
                value = spec.defaultValue ?? spec.display ?? "";
            }

            if (typeof value === "boolean") value = value ? "Yes" : "No";
            const valueStr = value === null || value === undefined ? "" : String(value).trim();
            const cleanLabel = String(label).trim().toLowerCase();
            if (cleanLabel.includes("report") || cleanLabel === "report incorrect specs") continue;

            out.push({
                label: String(label).trim(),
                value: valueStr,
                valueType: spec.valueType,
                unit: spec.unit,
                valueNormalized: spec.valueNormalized,
                raw: spec,
            });
        }

        return out;
    }

    // Try several possible places where specs might live
    const sections =
        src?.base_variant?.specs ||
        src?.specSections ||
        src?.base_variant?.specSections ||
        src?.specs ||
        [];

    if (!Array.isArray(sections)) return out;

    for (const category of sections) {
        const items = category?.specs || [];
        if (!Array.isArray(items)) continue;
        for (const spec of items) {
            // Determine label and value from multiple schema shapes
            const label = spec.label || spec.name || spec.key || spec.title;
            if (!label) continue;

            // Choose a display value. Prefer explicit string `value`, else normalize number+unit
            let value: any = null;
            if (spec.value !== undefined && spec.value !== null && String(spec.value).trim() !== "") {
                value = spec.value;
            } else if (spec.valueNormalized !== undefined && spec.valueNormalized !== null) {
                // When valueNormalized exists, prefer combining normalized + unit when unit present
                value = spec.unit ? `${spec.valueNormalized} ${spec.unit}` : String(spec.valueNormalized);
            } else {
                value = spec.defaultValue ?? spec.display ?? "";
            }

            // Normalize booleans to human friendly strings
            if (typeof value === "boolean") value = value ? "Yes" : "No";

            // Skip purely empty values
            const valueStr = value === null || value === undefined ? "" : String(value).trim();

            // Skip report / meta rows
            const cleanLabel = String(label).trim().toLowerCase();
            if (cleanLabel.includes("report") || cleanLabel === "report incorrect specs") continue;

            out.push({
                label: String(label).trim(),
                value: valueStr,
                valueType: spec.valueType,
                unit: spec.unit,
                valueNormalized: spec.valueNormalized,
                raw: spec,
            });
        }
    }

    return out;
}

// Reads the backend's dedicated featureSections[].features[] (each carrying
// an `available` boolean), independent from specSections — features are no
// longer inferred by regex-matching spec values.
export function processCarFeatures(modelData: any): { label: string }[] {
    const out: { label: string }[] = [];

    const src = modelData?.modelData ?? modelData?.model ?? modelData;

    const sections =
        src?.base_variant?.featureSections ||
        src?.featureSections ||
        src?.base_variant?.features ||
        src?.features ||
        [];

    if (!Array.isArray(sections)) return out;

    for (const section of sections) {
        const items = section?.features || [];
        if (!Array.isArray(items)) continue;
        for (const feature of items) {
            const label = feature?.label || feature?.name || feature?.key;
            if (!label) continue;

            const available = feature?.available;
            const isAvailable = available === true || available === "true" || available === 1;
            if (!isAvailable) continue;

            out.push({ label: String(label).trim() });
        }
    }

    return out;
}

// Explicit, vehicle-type-aware "Key Specs" selection — distinct from the
// generic top-N heuristic in processCarSpecs/displayCarSpecs. EVs and ICE
// vehicles surface different headline specs; seating capacity and body type
// are always shown regardless of vehicle type. A slot is skipped (not shown
// as "--") when the underlying spec doesn't exist for that vehicle.
const EV_KEY_SPEC_KEYS = ["motor_power", "max_power", "max_torque"];
const ICE_KEY_SPEC_KEYS = ["engine_displacement", "max_power", "max_torque"];

function resolveSpecValue(spec: any): string {
    let value: any = null;
    if (spec.value !== undefined && spec.value !== null && String(spec.value).trim() !== "") {
        value = spec.value;
    } else if (spec.valueNormalized !== undefined && spec.valueNormalized !== null) {
        value = spec.unit ? `${spec.valueNormalized} ${spec.unit}` : String(spec.valueNormalized);
    } else {
        value = spec.defaultValue ?? spec.display ?? "";
    }
    if (typeof value === "boolean") value = value ? "Yes" : "No";
    return value === null || value === undefined ? "" : String(value).trim();
}

function getSpecValueFromSections(sections: any[], key: string): string {
    if (!Array.isArray(sections)) return "";
    for (const section of sections) {
        for (const spec of section?.specs || []) {
            if (spec?.key === key) return resolveSpecValue(spec);
        }
    }
    return "";
}

// A model can span multiple distinct engines/motors across its variants
// (e.g. a lineup running a turbo-petrol I4 and a mild-hybrid V8) — this is
// the text-field equivalent of a numeric specs_range. When the full variant
// list is available, list every distinct value across it; otherwise fall
// back to just the base variant's single value.
function getDistinctSpecAcrossVariants(variants: any[] | undefined, fallbackSections: any[], key: string): string {
    if (Array.isArray(variants) && variants.length) {
        const seen = new Set<string>();
        const values: string[] = [];
        for (const variant of variants) {
            const value = getSpecValueFromSections(variant?.specSections || [], key);
            if (value && !seen.has(value)) {
                seen.add(value);
                values.push(value);
            }
        }
        if (values.length) return values.join(", ");
    }
    return getSpecValueFromSections(fallbackSections, key);
}

export function getCuratedKeySpecs(modelData: any, variants?: any[]): { label: string; value: string }[] {
    const out: { label: string; value: string }[] = [];
    const src = modelData?.modelData ?? modelData?.model ?? modelData;

    // Prefer the actually-viewed variant's specs (variant page: base_variant
    // holds the selected variant) over the model's own base-variant specs
    // (model page), so viewing a non-base trim never shows the wrong specs.
    const sections = src?.base_variant?.specSections || src?.specSections || [];

    const specsByKey = new Map<string, any>();
    if (Array.isArray(sections)) {
        for (const section of sections) {
            for (const spec of section?.specs || []) {
                if (spec?.key) specsByKey.set(spec.key, spec);
            }
        }
    }

    const fuelTypes: string[] = Array.isArray(src?.fuel_types) ? src.fuel_types : [];
    const isElectric = fuelTypes.some((f) => String(f).toLowerCase() === "electric");

    const desiredKeys = isElectric ? EV_KEY_SPEC_KEYS : ICE_KEY_SPEC_KEYS;
    const textTypeKey = isElectric ? "motor_type" : "engine_type";
    const textTypeLabel = isElectric ? "Motor Type" : "Engine Type";
    for (const key of desiredKeys) {
        const spec = specsByKey.get(key);
        if (!spec) continue;
        const value = resolveSpecValue(spec);
        if (!value) continue;
        out.push({ label: spec.label || key, value });

        // Text engine/motor type goes right after its numeric counterpart,
        // matching the legacy layout.
        if (key === desiredKeys[0]) {
            const textValue = getDistinctSpecAcrossVariants(variants, sections, textTypeKey);
            if (textValue) out.push({ label: textTypeLabel, value: textValue });
        }
    }

    // Always shown, regardless of vehicle type
    const seatingSpec = specsByKey.get("seating_capacity");
    const seatingValue = seatingSpec
        ? resolveSpecValue(seatingSpec)
        : String(
              src?.base_variant?.seating_capacity ??
                  (Array.isArray(src?.seating_capacity) ? src.seating_capacity[0] : src?.seating_capacity) ??
                  "",
          );
    if (seatingValue) out.push({ label: seatingSpec?.label ?? "Seating Capacity", value: seatingValue });

    const bodyTypeName = src?.body_types?.[0]?.name;
    if (bodyTypeName) out.push({ label: "Body Type", value: bodyTypeName });

    return out;
}

// Prepares car specs for display, handling both array and object map formats
export function displayCarSpecs(specs: any): { label: string; value: any }[] {
    // If already an array of normalized specs, return mapping to label/value
    if (Array.isArray(specs)) {
        return specs.map((s) => ({ label: s.label, value: s.value, valueType: s.valueType, unit: s.unit, valueNormalized: s.valueNormalized }));
    }

    // If it's an object map (older shape), convert to array
    if (specs && typeof specs === "object") {
        return Object.entries(specs).map(([label, value]) => ({ label, value }));
    }

    return [];
}

// Converts URLs in text to clickable links
export function linkify(text: any) {
    if (/<a\s/i.test(text)) return text;
    return text.replace(/\b(https?:\/\/[^\s]+|www\.[^\s]+)\b/g, (url: any) => {
        const href = url.startsWith("http") ? url : `https://${url}`;
        return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline">${url}</a>`;
    });
}

// Strips HTML tags from a string, returning plain text
export function stripHtmlTags(html: string): string {
    // Handle null/undefined/empty values
    if (!html || typeof html !== 'string') {
        return '';
    }

    // Use a deterministic, SSR-safe approach (avoid `document`) so
    // output is identical on server and client.
    let text = html.replace(/<[^>]*>/g, '');

    // Decode a few common HTML entities, otherwise replace unknown entities with a space
    const entityMap: Record<string, string> = {
        nbsp: ' ',
        amp: '&',
        lt: '<',
        gt: '>',
        quot: '"',
        apos: "'",
    };

    text = text.replace(/&([^;\s]+);/g, (match, name) => {
        if (name.startsWith('#')) return ' ';
        return entityMap[name] ?? ' ';
    });

    // Collapse whitespace and trim to normalize newlines/tabs/spaces
    return text.replace(/\s+/g, ' ').trim();
}

// Creates a plain text excerpt from HTML content, limited to maxLength
export function createExcerpt(content: string, maxLength: number = 150): string {
    // Handle null/undefined/empty values
    if (!content || typeof content !== 'string') {
        return '';
    }

    const cleanContent = stripHtmlTags(content);
    return cleanContent.length > maxLength
        ? cleanContent.substring(0, maxLength) + '...'
        : cleanContent;
}

export function extractH2Headings(html: string): string[] {
    const headings: string[] = [];
    const h2Regex = /<h2\b[^>]*>([\s\S]*?)<\/h2>/gi;
    const tagRegex = /<[^>]+>/g;

    let match: RegExpExecArray | null;

    while ((match = h2Regex.exec(html)) !== null) {
        const content = match[1];
        headings.push(
            content.includes("<")
                ? content.replace(tagRegex, "").trim()
                : content.trim()
        );
    }

    return headings;
}
